import { Dispatch } from "../../utility/common/storeHelper"
import { actions, IAIAssistHint } from "./slice"
import { handleHttpException } from "../handleHttpException"
import { JsAgentSWBridge } from "../../utility/service-workers"
import store from "../store"
import { dialogsApi } from "../../api/controllers/dialogs"
import {
    ApiMessage,
    ApiMessagesDU,
    isApiMessage,
    MessageCase,
    MessageDirection,
    UpdatedMessage
} from "../../models/Dialogs/message"
import callDebounced from "lodash/debounce"
import dialogsController from "../../api/controllers/dialogs-v1"
import { IAISuggestRequest } from "../../models/Dialogs/aiSuggestDTOs"
import { QueryStatus } from "@reduxjs/toolkit/dist/query"
import { isStarterMessage } from "../../api/hubs/helpers"
import { MESSAGES_PER_PAGE } from "../../utility/dialogs/constant"
import { saveNotificationInfo } from "../notifications/thunks"
import { apiInstance, apiInstanceV1 } from "../../api/api"
import { TFunction } from "i18next"
import { format } from "date-fns"
import {
    GetAIAssistHintDialogQueryArgs,
    MessageDatagramGpt,
    MessageDatagramMode,
    MessageDatagramPayload,
    MessageRequestTypesMode,
    MessageSide
} from "../../models/Dialogs/aiAssistant"
import { MESSAGE_DATAGRAM_TYPE } from "./constants"
import { selectCurrentOperatorMessage } from "./selectors"

const gptJsAgentBridge = new JsAgentSWBridge("", "gpt_assistant_agent")

const MAX_MESSAGES_AI_REQUEST = 10

const getAIAssistHintRootQuery = callDebounced(async (payload: MessageDatagramGpt, dispatch: Dispatch) => {
    try {
        dispatch(actions.getAIAssistHintProcess())

        const messageResp = await gptJsAgentBridge.postMessageToSW<MessageDatagramGpt, IAIAssistHint>({
            type: MESSAGE_DATAGRAM_TYPE.PROTO_PREDICATE,
            payload
        })

        dispatch(actions.getAIAssistHintSuccess(messageResp))
    } catch (e) {
        handleHttpException(e, "", actions.getAIAssistHintFailed, dispatch)
    }
}, 1000)

export const getAIAssistHintDialogQuery = (body: GetAIAssistHintDialogQueryArgs) => async (dispatch: Dispatch) => {
    const { projectId, omniUserId, dialogId, language, reqTypeCommand, nonActiveDialog } = body

    const messages: MessageDatagramPayload[] = []
    const operatorMessage = selectCurrentOperatorMessage(store.getState())

    if (!projectId) {
        return
    }

    gptJsAgentBridge.setCustomerId(projectId)

    const dialogMessagesResponse = await dispatch(
        dialogsApi.endpoints.getDialogMessages.initiate(
            {
                OmniUserId: omniUserId,
                Count: MESSAGES_PER_PAGE,
                FromTodayDialogs: false,
                StartTime: 0,
                ProjectId: projectId
            },
            {
                forceRefetch: true
            }
        )
    )

    const messagesHistory = dialogMessagesResponse.data?.Messages ?? []

    let i = 0
    while (messages.length < MAX_MESSAGES_AI_REQUEST && messagesHistory[i]) {
        const messageEntity = messagesHistory[i]

        const messageEntityFields = messageEntity.Fields as UpdatedMessage
        const direction = messageEntityFields.Direction

        if (messageEntity.Case === MessageCase.Message && !isStarterMessage(messageEntity)) {
            messages.push({
                side: direction ? MessageSide.OPERATOR : MessageSide.CLIENT,
                text: messageEntityFields.Text ?? "",
                timestamp: messageEntityFields.AddedDate
            })
        }
        i++
    }

    await getAIAssistHintRootQuery(
        {
            messages,
            operatorMessage,
            reqTypeCommand,
            dialog_id: dialogId,
            omni_user_id: omniUserId,
            language,
            nonActiveDialog,
            mode: MessageDatagramMode.GPT
        },
        dispatch
    )
}

export const getAIAssistHint =
    (
        omniUserId: string,
        projectId: string,
        nonActiveDialog = false,
        dialogId = "",
        language = "ru",
        reqTypeCommand = ""
    ) =>
    async (dispatch: Dispatch) => {
        await getAIAssistHintDialogQuery({
            projectId,
            omniUserId,
            dialogId,
            language,
            reqTypeCommand,
            nonActiveDialog
        })(dispatch)
    }

export const getAIAssistRequestTypes = () => async (dispatch: Dispatch) => {
    const projectId = store.getState().project.selectProject.data?.id

    if (!projectId) {
        return
    }

    gptJsAgentBridge.setCustomerId(projectId)

    try {
        dispatch(actions.getAIAssistRequestTypesProcess())

        const messageResp = await gptJsAgentBridge.postMessageToSW<MessageRequestTypesMode, string[]>({
            type: MESSAGE_DATAGRAM_TYPE.PROTO_PREDICATE,
            payload: {
                mode: MessageDatagramMode.GET_REQUEST_TYPE
            }
        })

        dispatch(actions.getAIAssistRequestTypesSuccess(messageResp))
    } catch (e) {
        handleHttpException(e, "", actions.getAIAssistRequestTypesFailed, dispatch)
    }
}

const getAISuggestsRootQuery = callDebounced(async (payload: IAISuggestRequest, dispatch: Dispatch) => {
    try {
        dispatch(actions.getAISuggestProcess())

        const aiSuggest = await dialogsController.getAISuggest(payload)

        dispatch(actions.getAISuggestSuccess(aiSuggest))
    } catch (e) {
        handleHttpException(e, "", err => actions.getAISuggestFailed(err), dispatch)
    }
}, 1000)

export const getAISuggestsByClientId = (omniUserId: string) => async (dispatch: Dispatch) => {
    const projectId = store.getState().project.selectProject.data?.id

    if (!projectId) {
        return
    }

    const getDialogMessagesRequestArgs = {
        OmniUserId: omniUserId,
        Count: 100,
        FromTodayDialogs: false,
        StartTime: 0,
        ProjectId: projectId
    }

    const messagesCached = dialogsApi.endpoints.getDialogMessages.select(getDialogMessagesRequestArgs)(store.getState())

    let messages: ApiMessagesDU[] = []

    if (messagesCached.status !== QueryStatus.fulfilled) {
        const result = await dispatch(dialogsApi.endpoints.getDialogMessages.initiate(getDialogMessagesRequestArgs))
        messages = result.data?.Messages ?? []
    } else {
        messages = messagesCached.data.Messages
    }

    const apiMessages = messages.filter(
        (msg): msg is ApiMessage =>
            isApiMessage(msg) && msg.Fields.Direction === MessageDirection.Incoming && !isStarterMessage(msg)
    )
    const lastMessage = apiMessages[0]
    const currTimeISO = new Date().toISOString()

    if (lastMessage) {
        await getAISuggestsRootQuery(
            {
                projectId,
                omniUserId,
                Added: currTimeISO,
                Actions: lastMessage.Fields.Actions,
                CallCenterOperatorId: "DEFAULT___test",
                Channel: 6,
                ChannelId: lastMessage.Fields.ChannelId,
                Context: lastMessage.Fields.Context,
                DialogID: lastMessage.Fields.DialogId,
                Direction: 1,
                Flags: [],
                IdInChannel: "",
                Info: "",
                IsClientServiceMessage: false,
                IsServiceMessage: false,
                Meta: lastMessage.Fields.Meta,
                MsgId: lastMessage.Fields.Id,
                MsgType: lastMessage.Fields.MessageType,
                OmniUserId: omniUserId,
                OperatorName: "",
                Text: lastMessage.Fields.Text ?? "",
                TextType: "Text",
                UserName: "",
                SlotContext: {},
                time: "",
                ChannelTitle: ""
            },
            dispatch
        )
    }
}

export const getAISuggestsByDialogId = (dialogId: string) => async (dispatch: Dispatch) => {
    const selectedDialogInstance = dialogsApi.endpoints.getDialog.select(dialogId)(store.getState())
    const omniUserId = selectedDialogInstance.data?.Client.OmniUserId

    if (omniUserId) {
        getAISuggestsByClientId(omniUserId)(dispatch)
    }
}

export const downloadDialogAttachments =
    ({ id, urls, singleFileName, t }: { id: string; urls: string[]; t: TFunction; singleFileName?: string }) =>
    async (dispatch: Dispatch) => {
        const tNotificationNamespace = "notification:export-attachments"

        const formattedDate = format(new Date(), "dd-MM-yy")
        const archiveFileName = `attachments-${id}-${formattedDate}.zip`

        const isMultipleFiles = !singleFileName || urls.length > 1
        const downloadUrlPath = isMultipleFiles ? "/dialog/get_archive" : `/dialogs/get_file${urls[0]}`
        const downloadFileName = isMultipleFiles ? archiveFileName : singleFileName ?? ""
        const correctApiInstance = isMultipleFiles ? apiInstance : apiInstanceV1
        const method = isMultipleFiles ? "POST" : "GET"

        try {
            saveNotificationInfo(dispatch, {
                Title: {
                    Value: "",
                    NeedLocalization: false
                },
                Type: "exportFiles"
            })

            dispatch(
                actions.setExportAttachmentsProgress({
                    progress: 1,
                    text: downloadFileName,
                    isError: false
                })
            )

            const response = await correctApiInstance({
                url: downloadUrlPath,
                method: method,
                data: {
                    urls
                },
                responseType: "blob",
                onDownloadProgress: progressEvent => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

                        dispatch(actions.setExportAttachmentsProgress({ progress: percentCompleted }))
                    }
                }
            })

            const url = window.URL.createObjectURL(response.data)
            const link = document.createElement("a")
            link.href = url
            link.download = downloadFileName

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            window.URL.revokeObjectURL(url)

            dispatch(
                actions.setExportAttachmentsProgress({
                    progress: 0,
                    text: t(`${tNotificationNamespace}.success`),
                    isError: false
                })
            )
        } catch (error) {
            dispatch(
                actions.setExportAttachmentsProgress({
                    progress: 0,
                    text: t(`${tNotificationNamespace}.error`),
                    isError: true
                })
            )
            console.error("Download attachments failed - ", error)
        }
    }
